/**
 * The words `Pagination` says — and the English it says them in when nobody
 * tells it otherwise.
 *
 * `design/` may not import a dictionary: a primitive that looks a sentence up
 * knows which app it is in (`.dependency-cruiser.cjs`, `design-is-independent`).
 * So it does not look one up — it TAKES one, and the default it falls back to is
 * the source language itself. A screen that passes nothing still reads correctly;
 * a screen that speaks i18n passes `t(...)` and the primitive never learns how.
 *
 * In a separate file for the same reason `data-list.ts` is: `<script setup>`
 * exports no type, and a documented contract eats a component's line budget.
 *
 * ── WHY EVERY LABEL IS A PLAIN STRING ─────────────────────────────────────
 *
 * Three of these sit NEXT to a number (`26–50 of 4312`, `Page 7`, `Go to page,
 * 1–160`). A label with a hole in it would need something to fill the hole, and
 * that something is an interpolator — a second, worse i18n living inside the
 * design system. The number stays outside the word instead, which is what both
 * languages already do.
 */
export type PaginationLabels = {
	/** Names the `<nav>` for whoever only hears the page. */
	nav: string;
	/** Joins the range to the total: `26–50 of 4312`. */
	of: string;
	/** The page-size chooser, offered only when the screen passes `pageSizes`. */
	perPage: string;
	previous: string;
	next: string;
	/** Precedes the number on each numbered button: `Page 7`. */
	page: string;
	/** The visible label of the jump box. */
	goTo: string;
	/** Names the jump box for a reader; the range `1–N` follows it. */
	goToPage: string;
};

export const paginationLabels: PaginationLabels = {
	nav: "Pagination",
	of: "of",
	perPage: "Per page",
	previous: "Previous",
	next: "Next",
	page: "Page",
	goTo: "Go to",
	goToPage: "Go to page",
};
